import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useSocket, { getSharedSocket } from "./useSocket";
import { getLocalStorageData } from "../shared/commonFunction";

const FETCH_GIFT_POPUPS_EVENT = "fetch-gift-popups";
const GIFT_POPUPS_EVENT = "gift-popups";

const getCurrentUserId = (userData) =>
  userData?._id || JSON.parse(getLocalStorageData("user") || "{}")?._id || null;

const getPopupId = (popup) => {
  const raw =
    popup?.transactionId ||
    popup?._id ||
    popup?.id ||
    popup?.giftId ||
    popup?.popupId ||
    popup?.gift?._id ||
    popup?.gift?.id ||
    null;
  if (!raw) return null;
  if (typeof raw === "object") return raw._id || raw.id || raw.$oid || null;
  return String(raw);
};

const extractPopups = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data.popups)) return data.popups.filter(Boolean);
  if (Array.isArray(data.gifts)) return data.gifts.filter(Boolean);
  if (Array.isArray(data.data)) return data.data.filter(Boolean);
  if (data.popup && typeof data.popup === "object") return extractPopups(data.popup);
  if (data.data && typeof data.data === "object") return extractPopups(data.data);
  if (
    data.transactionId ||
    data._id ||
    data.id ||
    data.giftId ||
    data.popupId ||
    data.sender ||
    data.receiver ||
    data.giftType
  ) {
    return [data];
  }
  if (data.gift && typeof data.gift === "object") return extractPopups(data.gift);
  return [];
};

const getSenderId = (popup) =>
  popup?.sender?._id || popup?.senderId || popup?.from?._id || popup?.fromId || null;

const getReceiverId = (popup) =>
  popup?.receiver?._id || popup?.receiverId || popup?.to?._id || popup?.toId || null;

/**
 * Single app-wide gift popup subscription.
 * Emits `fetch-gift-popups` with `{ language }` on connect / language change,
 * and listens for `gift-popups` from anywhere in the app.
 */
export default function useGiftPopupsListener(enabled = true) {
  const { i18n } = useTranslation();
  const { socketRef, isReady } = useSocket();
  const userData = useSelector((state) => state.user.userData);
  const [popups, setPopups] = useState([]);
  const currentUserId = getCurrentUserId(userData);
  const language = i18n.language === "ar" ? "ar" : "en";

  const mergePopups = useCallback(
    (incoming) => {
      const items = extractPopups(incoming);
      if (!items.length) return;

      setPopups((prev) => {
        const next = [...prev];
        const indexById = new Map(
          next.map((popup, index) => [getPopupId(popup) || popup._clientId, index])
        );

        items.forEach((item) => {
          const senderId = getSenderId(item);
          const receiverId = getReceiverId(item);

          if (currentUserId && senderId && String(senderId) === String(currentUserId)) {
            return;
          }
          if (currentUserId && receiverId && String(receiverId) !== String(currentUserId)) {
            return;
          }

          const id = getPopupId(item);
          if (id && indexById.has(id)) {
            next[indexById.get(id)] = { ...next[indexById.get(id)], ...item };
            return;
          }

          const keyed = { ...item, _clientId: id || `gift-${Date.now()}-${next.length}` };
          indexById.set(keyed._clientId, next.length);
          next.push(keyed);
        });

        return next;
      });
    },
    [currentUserId]
  );

  const dismissCurrent = useCallback(() => {
    setPopups((prev) => prev.slice(1));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setPopups([]);
      return;
    }

    const socket = socketRef.current || getSharedSocket();
    if (!socket) return;

    const requestGiftPopups = () => {
      socket.emit(FETCH_GIFT_POPUPS_EVENT, { language });
    };

    const handleGiftPopups = (data) => {
      console.log('FETCH_GIFT_POPUPS_EVENT: ', data);
      mergePopups(data);
      if (extractPopups(data).length) {
        window.dispatchEvent(new CustomEvent("gift-popups-received"));
      }
    };

    socket.off(GIFT_POPUPS_EVENT, handleGiftPopups);
    socket.on(GIFT_POPUPS_EVENT, handleGiftPopups);

    socket.off("connect", requestGiftPopups);
    socket.on("connect", requestGiftPopups);

    if (socket.connected) {
      requestGiftPopups();
    }

    return () => {
      socket.off(GIFT_POPUPS_EVENT, handleGiftPopups);
      socket.off("connect", requestGiftPopups);
    };
  }, [enabled, isReady, language, mergePopups, socketRef]);

  return { popups, dismissCurrent };
}
